import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Tabs, Tab, TextField, MenuItem, Button, Alert, Typography, Divider, Grid,
  Stack, Chip, IconButton, Checkbox, FormControlLabel, Tooltip, CircularProgress, InputAdornment
} from '@mui/material';
import { Delete, Login, Logout, Star, StarBorder, PersonAdd, Edit, InfoOutlined } from '@mui/icons-material';
import axios from 'axios';
import { useStages } from '../hooks/useStages';
import { useUnsavedGuard } from '../hooks/useUnsavedGuard';
import { LEARNER_PATHWAYS } from '../constants/pathways';
import { canView } from '../utils/permissions';
import { fmtMoney } from '../utils/currency';

const API = process.env.REACT_APP_API_URL;

const GENDERS       = ['Male', 'Female', 'Other'];
const STUDENT_TYPES = ['Boarder', 'Day Scholar'];
const STATUSES      = ['Active', 'Graduated', 'Transferred', 'Withdrawn'];
const RELATIONSHIPS = ['Father', 'Mother', 'Guardian', 'Grandfather', 'Grandmother', 'Aunt', 'Uncle', 'Sibling', 'Other'];
const TRANSPORT_STATUS = ['Pending', 'Active', 'Ended'];

const emptyTransport = {
  usesBus: false, usage: 'Two-way', oneWayLeg: 'Morning',
  pickupLocation: '', dropoffLocation: '', status: 'Pending',
};

const empty = {
  admNo: '', upi: '', assessmentNo: '', name: '', stage: '', classCode: '', pathway: '', studentType: 'Day Scholar', parentEmail: '',
  postingGroup: '', status: 'Active',
  dob: '', gender: '', nationality: '', denomination: '',
  admDate: '', homeAddress: '', homeDistrict: '', grade6District: '', previousSchool: '', kcpeScore: '',
  transport: { ...emptyTransport },
};

const emptyG = { relationship: 'Father', fullName: '', phone: '', email: '', occupation: '', isPrimary: false, canLogin: false };

const toDateInput = (v) => {
  if (!v) return '';
  try { return new Date(v).toISOString().split('T')[0]; } catch { return ''; }
};

const TabPanel = ({ value, index, children }) =>
  value === index ? <Box pt={2}>{children}</Box> : null;

const F = ({ label, name, value, onChange, type = 'text', select, children, disabled, required, sm = 6, tooltip, inputProps }) => (
  <Grid item xs={12} sm={sm}>
    <TextField select={select} label={label} name={name} value={value}
      onChange={onChange} type={type} fullWidth size="small"
      InputLabelProps={type === 'date' ? { shrink: true } : undefined}
      inputProps={inputProps}
      InputProps={tooltip ? {
        endAdornment: (
          <InputAdornment position="end">
            <Tooltip title={tooltip} arrow>
              <InfoOutlined fontSize="small" sx={{ color: 'text.disabled', cursor: 'help' }} />
            </Tooltip>
          </InputAdornment>
        )
      } : undefined}
      disabled={disabled} required={required}>
      {children}
    </TextField>
  </Grid>
);

const LearnersForm = ({ open, learner, onClose, initialTab = 0 }) => {
  const { stages, loading: stagesLoading } = useStages();
  const [tab,           setTab]          = useState(0);
  const [form,          setForm]         = useState(empty);
  const [classes,       setClasses]      = useState([]);
  const [postingGroups, setPostingGroups] = useState([]);
  const [busStages,     setBusStages]    = useState([]); // transport locations (bus stages)
  const [error,         setError]        = useState('');
  const [saving,        setSaving]       = useState(false);
  // Unsaved-changes guard: snapshot the form as it opened (see useUnsavedGuard).
  const [initialForm,   setInitialForm]  = useState(empty);

  // Guardians (many-to-many). In edit mode these are LearnerGuardian links from
  // the server; when admitting a new learner they are held locally and attached
  // right after the learner is created.
  const [guardians,   setGuardians]   = useState([]);   // server links
  const [pending,     setPending]     = useState([]);   // local (new learner)
  const [gForm,       setGForm]       = useState(emptyG);
  const [gErr,        setGErr]        = useState('');
  const [gMsg,        setGMsg]        = useState('');
  const [gBusy,       setGBusy]       = useState(false);

  // Guardian edit modal
  const [editGuardianOpen,    setEditGuardianOpen]    = useState(false);
  const [editGuardianData,    setEditGuardianData]    = useState(null);
  const [editGuardianForm,    setEditGuardianForm]    = useState({});
  const [editGuardianBusy,    setEditGuardianBusy]    = useState(false);

  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  useEffect(() => {
    // Unified BC-style posting groups of type Customer (the receivable control).
    axios.get(`${API}/api/academics/posting-groups?type=Customer`, { headers })
      .then(res => setPostingGroups(res.data))
      .catch(() => {});
    // Bus stages (transport locations) for the Transport tab. Needs fleet view —
    // only request when the user has it, so non-fleet users don't hit a 403.
    if (canView('fleet')) {
      axios.get(`${API}/api/fleet/locations`, { headers })
        .then(res => setBusStages(res.data))
        .catch(() => {});
    }
  }, []); // eslint-disable-line

  const loadGuardians = useCallback((admNo) => {
    axios.get(`${API}/api/guardians/learner/${admNo}`, { headers })
      .then(res => setGuardians(res.data.data || []))
      .catch(() => setGuardians([]));
  }, []); // eslint-disable-line

  useEffect(() => {
    if (!open) { setTab(0); setError(''); setGuardians([]); setPending([]); setGForm(emptyG); setGErr(''); setGMsg(''); return; }
    setTab(initialTab); // open directly on a requested tab (e.g. Guardians from the learner card)
    const init = learner ? {
      admNo:          learner.admNo          || '',
      upi:            learner.upi            || '',
      assessmentNo:   learner.assessmentNo   || '',
      name:           learner.name           || '',
      stage:          learner.stage          || '',
      classCode:      learner.classCode      || '',
      pathway:        learner.pathway        || '',
      studentType:    learner.studentType    || 'Day Scholar',
      parentEmail:    learner.parentEmail    || '',
      postingGroup:   learner.postingGroup   || '',
      status:         learner.status         || 'Active',
      dob:            toDateInput(learner.dob),
      gender:         learner.gender         || '',
      nationality:    learner.nationality    || '',
      denomination:   learner.denomination   || '',
      admDate:        toDateInput(learner.admDate),
      homeAddress:    learner.homeAddress    || '',
      homeDistrict:   learner.homeDistrict   || '',
      grade6District: learner.grade6District || '',
      previousSchool: learner.previousSchool || '',
      kcpeScore:      learner.kcpeScore ?? '',
      transport:      { ...emptyTransport, ...(learner.transport || {}) },
    } : { ...empty, transport: { ...emptyTransport } };
    setForm(init);
    setInitialForm(init);   // baseline for the unsaved-changes check
    if (learner) loadGuardians(learner.admNo);
  }, [open, learner, loadGuardians, initialTab]);

  // Fetch classes whenever the selected stage changes
  useEffect(() => {
    if (!form.stage) { setClasses([]); return; }
    axios.get(`${API}/api/academics/classes?stageCode=${form.stage}&active=true`, { headers })
      .then(res => setClasses(res.data))
      .catch(() => setClasses([]));
  }, [form.stage]); // eslint-disable-line

  const handle = (e) => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value, ...(name === 'stage' ? { classCode: '' } : {}) }));
  };

  // Transport (nested) field setter
  const setT = (patch) => setForm(f => ({ ...f, transport: { ...f.transport, ...patch } }));
  const [diffDropoff, setDiffDropoff] = useState(false);
  useEffect(() => {
    const t = form.transport || {};
    setDiffDropoff(!!t.dropoffLocation && t.dropoffLocation !== t.pickupLocation);
  }, [form.transport?.pickupLocation]); // eslint-disable-line

  // ── Guardian helpers ────────────────────────────────────────────────────────
  const gField = (e) => {
    const { name, value, checked, type } = e.target;
    setGForm(g => ({ ...g, [name]: type === 'checkbox' ? checked : value }));
  };

  const openEditGuardian = (g) => {
    setEditGuardianData(g);
    setEditGuardianForm({
      fullName: g.fullName || '',
      phone: g.phone || '',
      email: g.email || '',
      occupation: g.occupation || '',
    });
    setEditGuardianOpen(true);
  };

  const editGuardianField = (e) => {
    const { name, value } = e.target;
    setEditGuardianForm(f => ({ ...f, [name]: value }));
  };

  const saveEditGuardian = async () => {
    if (!editGuardianForm.fullName.trim()) {
      setGErr('Name is required');
      return;
    }
    setEditGuardianBusy(true);
    try {
      await axios.put(`${API}/api/guardians/${editGuardianData._id}`, editGuardianForm, { headers });
      setEditGuardianOpen(false);
      setEditGuardianData(null);
      loadGuardians(learner.admNo);
    } catch (err) {
      setGErr(err.response?.data?.error || 'Could not update guardian');
    } finally {
      setEditGuardianBusy(false);
    }
  };

  const guardianPayload = (g) => ({
    fullName: g.fullName.trim(), phone: g.phone, email: g.email,
    occupation: g.occupation, relationship: g.relationship,
    isPrimary: g.isPrimary, userEmail: g.canLogin ? g.email : '',
  });

  const portalMsg = (data, email) => {
    if (data?.portalTempPassword) {
      setGMsg(`Parent portal account created for ${email}. Temporary password: ${data.portalTempPassword} — share it securely; they must change it on first login.`);
    }
  };

  const addGuardian = async () => {
    setGErr(''); setGMsg('');
    if (!gForm.fullName.trim()) { setGErr('Name is required'); return; }
    if (gForm.canLogin && !gForm.email.trim()) { setGErr('An email is required to allow portal login'); return; }
    if (learner) {
      setGBusy(true);
      try {
        const r = await axios.post(`${API}/api/guardians/learner/${learner.admNo}`, guardianPayload(gForm), { headers });
        portalMsg(r.data, gForm.email);
        setGForm(emptyG);
        loadGuardians(learner.admNo);
      } catch (err) {
        setGErr(err.response?.data?.error || 'Could not add person');
      } finally { setGBusy(false); }
    } else {
      setPending(p => [...p, { ...gForm, tempId: Date.now() }]);
      setGForm(emptyG);
    }
  };

  const removeServerGuardian = async (guardianId) => {
    try {
      await axios.delete(`${API}/api/guardians/learner/${learner.admNo}/${guardianId}`, { headers });
      loadGuardians(learner.admNo);
    } catch { /* ignore */ }
  };

  const patchLink = async (guardianId, body) => {
    try {
      await axios.patch(`${API}/api/guardians/learner/${learner.admNo}/${guardianId}`, body, { headers });
      loadGuardians(learner.admNo);
    } catch { /* ignore */ }
  };

  // Toggle parent-portal access by setting/clearing the guardian's userEmail
  const togglePortal = async (g) => {
    if (!g.userEmail && !g.email) { setGErr(`Add an email for ${g.fullName} first to enable portal login`); return; }
    setGErr(''); setGMsg('');
    try {
      const r = await axios.put(`${API}/api/guardians/${g._id}`, { userEmail: g.userEmail ? '' : g.email }, { headers });
      portalMsg(r.data, g.email);
      loadGuardians(learner.admNo);
    } catch (err) {
      setGErr(err.response?.data?.error || 'Could not update portal access');
    }
  };

  const handleSave = async () => {
    if (!form.admNo.trim()) { setError('Admission Number is required'); setTab(0); return; }
    if (!form.name.trim())  { setError('Name is required'); setTab(0); return; }
    if (!form.stage)        { setError('Stage is required'); setTab(0); return; }
    // Normalize transport: require a bus stage when opted in; default drop-off to pickup.
    const t = form.transport || { ...emptyTransport };
    if (t.usesBus && !t.pickupLocation) { setError('Select a bus stage (pickup location) for the bus'); setTab(4); return; }
    const payload = {
      ...form,
      transport: {
        ...t,
        oneWayLeg: t.usage === 'One-way' ? t.oneWayLeg : '',
        dropoffLocation: diffDropoff ? t.dropoffLocation : t.pickupLocation,
      },
    };
    setSaving(true); setError('');
    try {
      if (learner) {
        await axios.put(`${API}/api/learners/${learner.admNo}`, payload, { headers });
      } else {
        await axios.post(`${API}/api/learners`, payload, { headers });
        // Attach any people added before the learner existed
        for (const g of pending) {
          await axios.post(`${API}/api/guardians/learner/${form.admNo}`, guardianPayload(g), { headers });
        }
      }
      onClose(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const rows = learner ? guardians : pending;

  // Unsaved-changes guard. Dirty when the form differs from its opening
  // snapshot, or a guardian has been staged (pending list) or half-typed.
  const { attemptClose, discardDialog } = useUnsavedGuard({
    open,
    value:        { form, pending, gDraft: gForm },
    initialValue: { form: initialForm, pending: [], gDraft: emptyG },
    onClose:      () => onClose(false),
    message: `You have ${learner ? 'edited this learner' : 'entered new learner details'} that haven't been saved. Close without saving?`,
  });

  // Pathways only apply to Senior School (G10–G12) — show the field only there.
  const isSeniorStage = stages.find(s => s.code === form.stage)?.level === 'Senior Secondary';

  return (
    <Dialog open={open} onClose={attemptClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {learner ? `Edit Learner — ${learner.admNo}` : 'Admit New Learner'}
      </DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
        <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto" sx={{ mb: 1 }}>
          <Tab label="Basic" />
          <Tab label="Personal" />
          <Tab label="Admission" />
          <Tab label={`Guardians${rows.length ? ` (${rows.length})` : ''}`} />
          <Tab label="Transport" />
        </Tabs>

        {/* ── Tab 0: Basic ── */}
        <TabPanel value={tab} index={0}>
          <Grid container spacing={2}>
            <F label="Admission No *" name="admNo" value={form.admNo} onChange={handle} disabled={!!learner} required sm={4} />
            <F label="ULA" name="upi" value={form.upi} onChange={handle} sm={4}
              tooltip="Unique Personal Identifier" />
            <F label="Assessment No" name="assessmentNo" value={form.assessmentNo} onChange={handle} sm={4}
              tooltip="KNEC assessment number (CBC/CBE)" />
            <F label="Full Name *" name="name" value={form.name} onChange={handle} required sm={4} />
            <F label="Stage *" name="stage" value={form.stage} onChange={handle} select required sm={4}>
              {stagesLoading
                ? <MenuItem disabled><em>Loading…</em></MenuItem>
                : stages.map(s => <MenuItem key={s.code} value={s.code}>{s.code} — {s.name}</MenuItem>)}
            </F>
            <F label="Class" name="classCode" value={form.classCode} onChange={handle} select sm={4}>
              <MenuItem value=""><em>— None —</em></MenuItem>
              {classes.map(c => (
                <MenuItem key={c._id} value={c.classCode}>{c.className}</MenuItem>
              ))}
            </F>
            {isSeniorStage && (
              <F label="Pathway" name="pathway" value={form.pathway} onChange={handle} select sm={4}
                 tooltip="Senior-school pathway (G10–G12). Decides which pathway subjects the learner is registered for (e.g. STEM → Essential Maths, Social Sciences → Core Maths).">
                <MenuItem value=""><em>— Not set —</em></MenuItem>
                {LEARNER_PATHWAYS.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
              </F>
            )}
            <F label="Student Type" name="studentType" value={form.studentType} onChange={handle} select sm={4}>
              {STUDENT_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </F>
            <F label="Status" name="status" value={form.status} onChange={handle} select sm={6}>
              {STATUSES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </F>
            <F label="Posting Group *" name="postingGroup" value={form.postingGroup} onChange={handle} select sm={6}
               helperText="Customer posting group — required for billing">
              {postingGroups.length === 0
                ? <MenuItem value=""><em>— none defined —</em></MenuItem>
                : postingGroups.map(g => <MenuItem key={g.code} value={g.code}>{g.code}{g.description ? ` — ${g.description}` : ''}</MenuItem>)}
            </F>
          </Grid>
        </TabPanel>

        {/* ── Tab 1: Personal ── */}
        <TabPanel value={tab} index={1}>
          <Grid container spacing={2}>
            <F label="Date of Birth" name="dob" value={form.dob} onChange={handle} type="date" sm={4} />
            <F label="Gender" name="gender" value={form.gender} onChange={handle} select sm={4}>
              <MenuItem value=""><em>—</em></MenuItem>
              {GENDERS.map(g => <MenuItem key={g} value={g}>{g}</MenuItem>)}
            </F>
            <F label="Nationality" name="nationality" value={form.nationality} onChange={handle} sm={4} />
            <F label="Denomination / Religion" name="denomination" value={form.denomination} onChange={handle} sm={6} />
          </Grid>
        </TabPanel>

        {/* ── Tab 2: Admission ── */}
        <TabPanel value={tab} index={2}>
          <Grid container spacing={2}>
            <F label="Admission Date" name="admDate" value={form.admDate} onChange={handle} type="date" sm={4} />
            <F label="Previous School" name="previousSchool" value={form.previousSchool} onChange={handle} sm={5} />
            <F label="KCPE Score" name="kcpeScore" value={form.kcpeScore} onChange={handle} type="number" sm={3}
              inputProps={{ step: 'any', min: 0 }} />
            <F label="Home Address" name="homeAddress" value={form.homeAddress} onChange={handle} sm={12} />
            <F label="Home District" name="homeDistrict" value={form.homeDistrict} onChange={handle} sm={6} />
            <F label="Grade 6 District" name="grade6District" value={form.grade6District} onChange={handle} sm={6} />
          </Grid>
        </TabPanel>

        {/* ── Tab 3: Guardians (parents / guardians / relatives) ── */}
        <TabPanel value={tab} index={3}>
          <Typography variant="body2" color="text.secondary" mb={1}>
            Add every person related to this learner. Pick their relationship. Mark
            those who may <b>log in to the parent portal</b> to view this learner.
          </Typography>
          {gErr && <Alert severity="error" sx={{ mb: 1 }}>{gErr}</Alert>}
          {gMsg && <Alert severity="success" sx={{ mb: 1 }} onClose={() => setGMsg('')}>{gMsg}</Alert>}

          {/* Add person */}
          <Box sx={{ p: 2, mb: 2, bgcolor: '#f7f9fb', borderRadius: 2 }}>
            <Grid container spacing={1.5} alignItems="center">
              <Grid item xs={12} sm={3}>
                <TextField select label="Relationship" name="relationship" value={gForm.relationship}
                  onChange={gField} fullWidth size="small">
                  {RELATIONSHIPS.map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField label="Full Name" name="fullName" value={gForm.fullName} onChange={gField} fullWidth size="small" />
              </Grid>
              <Grid item xs={12} sm={5}>
                <TextField label="Phone" name="phone" value={gForm.phone} onChange={gField} fullWidth size="small" />
              </Grid>
              <Grid item xs={12} sm={5}>
                <TextField label="Email" name="email" value={gForm.email} onChange={gField} type="email" fullWidth size="small" />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField label="Occupation" name="occupation" value={gForm.occupation} onChange={gField} fullWidth size="small" />
              </Grid>
              <Grid item xs={6} sm="auto">
                <FormControlLabel
                  control={<Checkbox size="small" name="isPrimary" checked={gForm.isPrimary} onChange={gField} />}
                  label="Primary" />
              </Grid>
              <Grid item xs={6} sm="auto">
                <FormControlLabel
                  control={<Checkbox size="small" name="canLogin" checked={gForm.canLogin} onChange={gField} />}
                  label="Can log in" />
              </Grid>
              <Grid item xs={12} sm="auto">
                <Button variant="contained" size="small" startIcon={gBusy ? <CircularProgress size={16} /> : <PersonAdd />}
                  onClick={addGuardian} disabled={gBusy}>Add</Button>
              </Grid>
            </Grid>
          </Box>

          {/* People list */}
          {rows.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
              No related people yet. Add parents, guardians or relatives above.
            </Typography>
          ) : (
            <Stack divider={<Divider />} spacing={0}>
              {rows.map((row) => {
                // Normalize server-link vs local-pending shape
                const g   = learner ? (row.guardian || {}) : row;
                const gid = learner ? g._id : row.tempId;
                const rel = row.relationship;
                const isPrimary = row.isPrimary;
                const canLogin  = learner ? !!g.userEmail : !!row.canLogin;
                return (
                  <Box key={gid} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
                    {learner ? (
                      <TextField select size="small" value={rel} sx={{ width: 140 }}
                        onChange={(e) => patchLink(gid, { relationship: e.target.value })}>
                        {RELATIONSHIPS.map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
                      </TextField>
                    ) : (
                      <Chip label={rel} size="small" sx={{ width: 140 }} />
                    )}

                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={600} noWrap>
                        {g.fullName || '—'}
                        {isPrimary && <Chip label="Primary" size="small" color="primary" sx={{ ml: 1, height: 18 }} />}
                        {canLogin && <Chip label="Portal" size="small" color="success" sx={{ ml: 0.5, height: 18 }} />}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {[g.phone, g.email, g.occupation].filter(Boolean).join(' · ') || 'No contact details'}
                      </Typography>
                    </Box>

                    {learner && (
                      <>
                        <Tooltip title={isPrimary ? 'Primary contact' : 'Make primary'}>
                          <IconButton size="small" color={isPrimary ? 'primary' : 'default'}
                            onClick={() => !isPrimary && patchLink(gid, { isPrimary: true })}>
                            {isPrimary ? <Star fontSize="small" /> : <StarBorder fontSize="small" />}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={canLogin ? 'Disable portal login' : 'Allow portal login'}>
                          <IconButton size="small" color={canLogin ? 'success' : 'default'} onClick={() => togglePortal(g)}>
                            {canLogin ? <Logout fontSize="small" /> : <Login fontSize="small" />}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit details">
                          <IconButton size="small" color="default" onClick={() => openEditGuardian(g)}>
                            <Edit fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                    <Tooltip title="Remove">
                      <IconButton size="small" color="error"
                        onClick={() => learner ? removeServerGuardian(gid) : setPending(p => p.filter(x => x.tempId !== gid))}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                );
              })}
            </Stack>
          )}
        </TabPanel>

        {/* ── Tab 4: Transport (Fleet) ── */}
        <TabPanel value={tab} index={4}>
          <FormControlLabel
            control={<Checkbox checked={!!form.transport?.usesBus}
              onChange={e => setT({ usesBus: e.target.checked })} />}
            label="This learner uses the school bus" />
          {form.transport?.usesBus && (
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <F label="Usage" name="usage" select sm={4}
                value={form.transport.usage} onChange={e => setT({ usage: e.target.value })}>
                <MenuItem value="Two-way">Two-way (pickup &amp; drop-off)</MenuItem>
                <MenuItem value="One-way">One-way</MenuItem>
              </F>
              {form.transport.usage === 'One-way' && (
                <F label="One-way leg" name="oneWayLeg" select sm={4}
                  value={form.transport.oneWayLeg} onChange={e => setT({ oneWayLeg: e.target.value })}>
                  <MenuItem value="Morning">Morning only</MenuItem>
                  <MenuItem value="Evening">Evening only</MenuItem>
                </F>
              )}
              <F label="Status" name="tstatus" select sm={4}
                value={form.transport.status} onChange={e => setT({ status: e.target.value })}>
                {TRANSPORT_STATUS.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              </F>

              <F label="Bus Stage (pickup location)" name="pickupLocation" select sm={6}
                value={form.transport.pickupLocation} onChange={e => setT({ pickupLocation: e.target.value })}
                helperText={busStages.length === 0 ? 'No bus stages set up yet (Fleet → Bus Stages)' : 'A "stage" is the bus stop'}>
                <MenuItem value=""><em>— select —</em></MenuItem>
                {busStages.map(s => (
                  <MenuItem key={s.code} value={s.code}>{s.name} — {fmtMoney(s.monthlyFee)}/mo</MenuItem>
                ))}
              </F>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={<Checkbox checked={diffDropoff}
                    onChange={e => { setDiffDropoff(e.target.checked); if (!e.target.checked) setT({ dropoffLocation: '' }); }} />}
                  label="Drop-off at a different stage" />
              </Grid>
              {diffDropoff && (
                <F label="Drop-off Stage" name="dropoffLocation" select sm={6}
                  value={form.transport.dropoffLocation} onChange={e => setT({ dropoffLocation: e.target.value })}>
                  <MenuItem value=""><em>— select —</em></MenuItem>
                  {busStages.map(s => <MenuItem key={s.code} value={s.code}>{s.name}</MenuItem>)}
                </F>
              )}
              <Grid item xs={12}>
                <Alert severity="info" sx={{ mt: 1 }}>
                  The monthly transport fee for the selected stage is added automatically to this learner's
                  term invoice (one-way learners pay a reduced share). Assign the learner to a specific bus in
                  <b> Fleet → Grouping &amp; Allocation</b>.
                </Alert>
              </Grid>
            </Grid>
          )}
        </TabPanel>
      </DialogContent>
      <DialogActions>
        <Button onClick={attemptClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving…' : learner ? 'Update' : 'Admit Learner'}
        </Button>
      </DialogActions>

      {/* ── Unsaved-changes confirmation (shared hook) ── */}
      {discardDialog}

      {/* ── Edit Guardian Modal ── */}
      <Dialog open={editGuardianOpen} onClose={() => setEditGuardianOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Guardian Details</DialogTitle>
        <DialogContent dividers>
          {editGuardianData && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              <TextField
                label="Full Name"
                name="fullName"
                value={editGuardianForm.fullName}
                onChange={editGuardianField}
                fullWidth
                required
              />
              <TextField
                label="Phone"
                name="phone"
                value={editGuardianForm.phone}
                onChange={editGuardianField}
                fullWidth
              />
              <TextField
                label="Email"
                name="email"
                type="email"
                value={editGuardianForm.email}
                onChange={editGuardianField}
                fullWidth
              />
              <TextField
                label="Occupation"
                name="occupation"
                value={editGuardianForm.occupation}
                onChange={editGuardianField}
                fullWidth
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditGuardianOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={saveEditGuardian} disabled={editGuardianBusy}>
            {editGuardianBusy ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default LearnersForm;
